<template>
  <div class="chart-container">
    <div class="chart-header">
      <div class="chart-title">
        <i class="fa" :class="chartIcon"></i>
        {{ chartTitle }}
        <span class="badge badge-primary ml-2">{{ displayedClients }} Clients</span>
        <span class="badge badge-info ml-1" v-if="chartType === 'bar'">{{ dynamicChartHeight }}px</span>
      </div>
      <div class="chart-controls">
        <label class="control-label">Show Top:</label>
        <select v-model="selectedLimit" @change="updateChart" class="form-control form-control-sm">
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="30">30</option>
          <option value="50">50</option>
          <option value="100">100</option>
          <option value="all">All</option>
        </select>
      </div>
    </div>
    
    <div class="chart-wrapper">
      <apexchart 
        :type="chartType"
        :height="dynamicChartHeight"
        :options="apexOptions"
        :series="apexSeries"
      />
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'ClientTeusFlexibleChart',
  
  components: {
    apexchart: VueApexCharts
  },
  
  props: {
    chartData: {
      type: Object,
      required: true
    },
    chartHeight: {
      type: Number,
      default: 400
    },
    chartType: {
      type: String,
      default: 'bar'
    },
    chartTitle: {
      type: String,
      default: 'Clients TEUs'
    },
    chartIcon: {
      type: String,
      default: 'fa-bar-chart'
    },
    defaultLimit: {
      type: Number,
      default: 10
    }
  },

  data() {
    return {
      selectedLimit: this.defaultLimit.toString()
    }
  },

  computed: {
    displayedClients() {
      if (this.selectedLimit === 'all') {
        return this.chartData.labels ? this.chartData.labels.length : 0
      }
      return Math.min(parseInt(this.selectedLimit), this.chartData.labels ? this.chartData.labels.length : 0)
    },

    dynamicChartHeight() {
      const baseHeight = this.chartHeight
      const clientCount = this.displayedClients
      
      if (this.chartType === 'pie') {
        // Pie charts don't need height adjustment
        return baseHeight
      }
      
      if (this.chartType === 'bar') {
        // For bar charts, increase height based on number of clients
        const heightPerClient = 25 // 25px per client
        const minHeight = 300
        const maxHeight = 800
        const calculatedHeight = baseHeight + (clientCount * heightPerClient)
        
        return Math.max(minHeight, Math.min(maxHeight, calculatedHeight))
      }
      
      return baseHeight
    },

    filteredChartData() {
      if (!this.chartData || !this.chartData.labels || !this.chartData.datasets) {
        return { labels: [], datasets: [] }
      }

      let limit = this.selectedLimit === 'all' ? this.chartData.labels.length : parseInt(this.selectedLimit)
      
      const filteredData = {
        labels: this.chartData.labels.slice(0, limit),
        datasets: this.chartData.datasets.map(dataset => {
          return Object.assign({}, dataset, {
            data: dataset.data.slice(0, limit)
          })
        })
      }

      // Debug log for pie chart data
      if (this.chartType === 'pie') {
        console.log('Pie Chart Data:', filteredData)
      }

      return filteredData
    },

    apexSeries() {
      if (!this.filteredChartData || !this.filteredChartData.datasets || !this.filteredChartData.datasets[0]) {
        return []
      }
      
      if (this.chartType === 'pie') {
        // For pie charts, return the data array directly
        const data = this.filteredChartData.datasets[0].data || []
        console.log('Pie Chart Series Data:', data)
        return data
      }
      
      return [{
        name: this.filteredChartData.datasets[0].label || 'Total TEUs',
        data: this.filteredChartData.datasets[0].data || []
      }]
    },
    
    apexOptions() {
      const isHorizontal = this.chartType === 'bar'
      const isPie = this.chartType === 'pie'
      
      if (isPie) {
        return {
          chart: {
            type: 'pie',
            toolbar: {
              show: true,
              tools: {
                download: true,
                zoom: true,
                zoomin: true,
                zoomout: true,
                pan: false,
                reset: true
              }
            }
          },
          plotOptions: {
            pie: {
              donut: {
                size: '60%'
              },
              expandOnClick: true,
              customScale: 1
            }
          },
          dataLabels: {
            enabled: true,
            formatter: function (val, opts) {
              const label = opts.w.config.labels[opts.seriesIndex]
              return label + ': ' + val.toFixed(1) + '%'
            },
            style: {
              fontSize: '11px',
              colors: ['#fff'],
              fontWeight: 'bold'
            }
          },
          labels: this.filteredChartData.labels || [],
          legend: {
            show: true,
            position: 'bottom',
            horizontalAlign: 'center',
            fontSize: '11px',
            fontFamily: 'Helvetica, Arial',
            fontWeight: 400,
            formatter: function(seriesName, opts) {
              const value = opts.w.globals.series[opts.seriesIndex]
              if (typeof value === 'number') {
                return seriesName + ': ' + value + ' TEUs'
              }
              return seriesName
            }
          },
          tooltip: {
            y: {
              formatter: function (val) {
                return val + ' TEUs'
              }
            }
          },
          colors: [
            '#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0', 
            '#546E7A', '#26a69a', '#D10CE8', '#FF6B6B', '#4ECDC4',
            '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8',
            '#F7DC6F', '#BB8FCE', '#85C1E9', '#F8C471', '#82E0AA',
            '#FFA07A', '#20B2AA', '#87CEEB', '#DDA0DD', '#98FB98',
            '#F0E68C', '#FFB6C1', '#E0E0E0', '#FF6347', '#32CD32'
          ]
        }
      }
      
      return {
        chart: {
          type: this.chartType,
          toolbar: {
            show: true,
            tools: {
              download: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          }
        },
        plotOptions: {
          bar: {
            horizontal: isHorizontal,
            columnWidth: isHorizontal ? '60%' : '55%',
            distributed: true,
            borderRadius: 4,
            dataLabels: {
              position: isHorizontal ? 'top' : 'top'
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val + ' TEUs'
          },
          offsetX: isHorizontal ? 0 : 0,
          offsetY: isHorizontal ? -20 : -20,
          style: {
            fontSize: '11px',
            colors: ['#304758'],
            fontWeight: 'bold'
          }
        },
        stroke: {
          show: true,
          width: 1,
          colors: ['#fff']
        },
        xaxis: {
          categories: this.filteredChartData.labels || [],
          labels: {
            style: {
              fontSize: '10px'
            },
            formatter: function(value) {
              // Truncate long client names
              if (value.length > (isHorizontal ? 15 : 12)) {
                return value.substring(0, (isHorizontal ? 15 : 12)) + '...'
              }
              return value
            },
            rotate: isHorizontal ? 0 : -45,
            rotateAlways: isHorizontal ? false : true
          },
          title: {
            text: isHorizontal ? 'Total TEUs' : 'Clients',
            style: {
              fontSize: '12px',
              fontWeight: 'bold'
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              fontSize: '10px'
            }
          },
          title: {
            text: isHorizontal ? 'Clients' : 'Total TEUs',
            style: {
              fontSize: '12px',
              fontWeight: 'bold'
            }
          }
        },
        fill: {
          opacity: 1
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + ' TEUs'
            }
          }
        },
        colors: [
          '#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0', 
          '#546E7A', '#26a69a', '#D10CE8', '#FF6B6B', '#4ECDC4',
          '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8',
          '#F7DC6F', '#BB8FCE', '#85C1E9', '#F8C471', '#82E0AA',
          '#FFA07A', '#20B2AA', '#87CEEB', '#DDA0DD', '#98FB98',
          '#F0E68C', '#FFB6C1', '#E0E0E0', '#FF6347', '#32CD32'
        ],
        legend: {
          show: false
        },
        grid: {
          borderColor: '#e7e7e7',
          strokeDashArray: 3
        }
      }
    }
  },

  methods: {
    updateChart() {
      // Chart will automatically update due to computed properties
      this.$emit('limit-changed', {
        limit: this.selectedLimit,
        displayedCount: this.displayedClients,
        chartHeight: this.dynamicChartHeight
      })
    }
  },

  watch: {
    dynamicChartHeight(newHeight) {
      // Emit height change for parent components
      this.$emit('height-changed', {
        height: newHeight,
        clientCount: this.displayedClients
      })
    }
  }
}
</script>

<style scoped>
.chart-container {
  position: relative;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #007bff;
}

.chart-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  margin: 0;
}

.chart-title i {
  margin-right: 8px;
  color: #007bff;
}

.chart-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-label {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin: 0;
}

.chart-wrapper {
  position: relative;
  transition: height 0.3s ease-in-out;
  overflow: hidden;
}

@media (max-width: 768px) {
  .chart-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .chart-controls {
    width: 100%;
    justify-content: space-between;
  }
  
  .chart-title {
    font-size: 16px;
  }
}
</style>



// WEBPACK FOOTER //
// ClientTeusFlexibleChart.vue?2d4a736b